/**
 * <announcement-rotator> — the cycling message column in the announcement bar.
 *
 * Ported from the rotation logic in the Next.js storefront's
 * src/components/top-bar.tsx: the current message slides DOWN and fades out,
 * then the next slides in from ABOVE, on a fixed interval. Timings come from
 * the section's settings via data attributes (data-interval, data-duration),
 * so staff tune them in the theme editor rather than in code.
 *
 * The markup is rendered by Liquid with every message already in place — the
 * first carrying data-phase="show", the rest data-phase="idle" — so the bar is
 * correct before this file runs, and stays correct if it never does.
 *
 * Behaviour this adds over the source component:
 *   - pauses while the pointer is over the column or a link inside it has
 *     keyboard focus, so a CTA can actually be clicked;
 *   - pauses while the tab is in the background;
 *   - does not animate at all under prefers-reduced-motion — the first message
 *     simply stays put;
 *   - never starts when there is a single message;
 *   - in the theme editor, selecting a message block shows that message and
 *     holds it there while it is selected.
 */
(function () {
  var PHASE_SHOW = 'show';
  var PHASE_OUT = 'out';
  var PHASE_IN = 'in';
  var PHASE_IDLE = 'idle';

  var reducedMotion =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  function prefersReducedMotion() {
    return Boolean(reducedMotion && reducedMotion.matches);
  }

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.messages = Array.prototype.slice.call(this.querySelectorAll('[data-message]'));
      if (this.messages.length === 0) return;

      this.interval = parseInt(this.dataset.interval, 10);
      if (isNaN(this.interval) || this.interval < 1000) this.interval = 5000;

      this.duration = parseInt(this.dataset.duration, 10);
      if (isNaN(this.duration) || this.duration < 0) this.duration = 600;

      this.index = 0;
      for (var i = 0; i < this.messages.length; i++) {
        if (this.messages[i].getAttribute('data-phase') === PHASE_SHOW) {
          this.index = i;
          break;
        }
      }

      this.paused = false;
      this.timer = null;
      this.swapTimer = null;

      this.handlePause = this.pause.bind(this);
      this.handleResume = this.resume.bind(this);
      this.handleVisibility = this.onVisibilityChange.bind(this);
      this.handleMotionChange = this.refresh.bind(this);
      this.handleBlockSelect = this.onBlockSelect.bind(this);
      this.handleBlockDeselect = this.onBlockDeselect.bind(this);

      this.addEventListener('mouseenter', this.handlePause);
      this.addEventListener('mouseleave', this.handleResume);
      this.addEventListener('focusin', this.handlePause);
      this.addEventListener('focusout', this.handleResume);
      document.addEventListener('visibilitychange', this.handleVisibility);

      if (reducedMotion) {
        if (typeof reducedMotion.addEventListener === 'function') {
          reducedMotion.addEventListener('change', this.handleMotionChange);
        } else if (typeof reducedMotion.addListener === 'function') {
          reducedMotion.addListener(this.handleMotionChange);
        }
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }

      this.refresh();
    }

    disconnectedCallback() {
      this.stop();

      this.removeEventListener('mouseenter', this.handlePause);
      this.removeEventListener('mouseleave', this.handleResume);
      this.removeEventListener('focusin', this.handlePause);
      this.removeEventListener('focusout', this.handleResume);
      document.removeEventListener('visibilitychange', this.handleVisibility);

      if (reducedMotion) {
        if (typeof reducedMotion.removeEventListener === 'function') {
          reducedMotion.removeEventListener('change', this.handleMotionChange);
        } else if (typeof reducedMotion.removeListener === 'function') {
          reducedMotion.removeListener(this.handleMotionChange);
        }
      }

      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    /** Whether this instance should be cycling at all, right now. */
    get canRotate() {
      return (
        this.dataset.rotate === 'true' &&
        this.messages.length > 1 &&
        !prefersReducedMotion()
      );
    }

    /** Start or stop cycling to match the current settings and preferences. */
    refresh() {
      if (this.canRotate && !this.paused) {
        this.schedule();
      } else {
        this.stop();
      }
    }

    schedule() {
      this.clearSchedule();
      var self = this;
      this.timer = window.setTimeout(function () {
        self.advance();
      }, this.interval);
    }

    clearSchedule() {
      if (this.timer !== null) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    stop() {
      this.clearSchedule();
      // Deliberately NOT clearing swapTimer: a swap already in flight must be
      // allowed to finish, or the bar is left showing a message that has
      // slid off-stage.
    }

    pause() {
      this.paused = true;
      this.clearSchedule();
    }

    resume() {
      this.paused = false;
      if (this.canRotate && this.timer === null) this.schedule();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    }

    advance() {
      if (!this.canRotate) return;

      // Re-arm first, so the cycle stays exactly `interval` long regardless of
      // how long the slide takes — the setInterval cadence the source had.
      this.schedule();

      var current = this.messages[this.index];
      var nextIndex = (this.index + 1) % this.messages.length;
      var next = this.messages[nextIndex];
      if (!current || !next || current === next) return;

      current.setAttribute('data-phase', PHASE_OUT);

      var self = this;
      if (this.swapTimer !== null) window.clearTimeout(this.swapTimer);
      this.swapTimer = window.setTimeout(function () {
        self.swapTimer = null;
        self.hide(current);
        self.index = nextIndex;
        self.reveal(next);
      }, this.duration);
    }

    /** Park a message off-stage and out of the accessibility tree. */
    hide(message) {
      message.setAttribute('data-phase', PHASE_IDLE);
      message.setAttribute('aria-hidden', 'true');
      message.setAttribute('inert', '');
    }

    /**
     * Bring a message in from above. It is placed at its starting position
     * with transitions suppressed, then released on the next frame so the
     * slide actually animates instead of snapping into place.
     */
    reveal(message, animate) {
      message.removeAttribute('aria-hidden');
      message.removeAttribute('inert');
      message.style.transition = 'none';
      message.setAttribute('data-phase', animate === false ? PHASE_SHOW : PHASE_IN);
      // Force a reflow so the browser registers the start position.
      void message.offsetHeight;
      message.style.transition = '';
      if (animate !== false) message.setAttribute('data-phase', PHASE_SHOW);
    }

    /** Jump straight to one message, no animation. Used by the theme editor. */
    show(index) {
      for (var i = 0; i < this.messages.length; i++) {
        if (i === index) {
          this.reveal(this.messages[i], false);
        } else {
          this.hide(this.messages[i]);
        }
      }
      this.index = index;
    }

    onBlockSelect(event) {
      if (!event.detail || !event.detail.blockId) return;
      for (var i = 0; i < this.messages.length; i++) {
        if (this.messages[i].getAttribute('data-block-id') === event.detail.blockId) {
          this.pause();
          this.show(i);
          return;
        }
      }
    }

    onBlockDeselect(event) {
      if (!event.detail || !event.detail.blockId) return;
      for (var i = 0; i < this.messages.length; i++) {
        if (this.messages[i].getAttribute('data-block-id') === event.detail.blockId) {
          this.resume();
          return;
        }
      }
    }
  }

  if (!window.customElements.get('announcement-rotator')) {
    window.customElements.define('announcement-rotator', AnnouncementRotator);
  }
})();
